import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, notification } from "antd";
import { Plus, Briefcase, Loader2, X } from "lucide-react";
import Oportunidade from "../../Components/Parceiro/Oportunidade";
import AindaSem from "../../Components/Graduado/AindaSem";
import { getMyJobs } from "../../API/getters";
import api from "../../API/api";

const { TextArea } = Input;

export default function ParceiroPage() {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingVaga, setEditingVaga] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [vagas, setVagas] = useState([]);

  useEffect(() => {
    const fetchVagas = async () => {
      const jobs = await getMyJobs();
      setVagas(jobs);
      console.log(jobs);
      setPageLoading(false);
    };
    fetchVagas();
  }, []);

  const handleAddVaga = () => {
    setEditingVaga(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (id) => {
    const vaga = vagas.find((v) => v.uuid === id);
    if (!vaga) {
      notification.error({
        message: "Erro",
        description: "Vaga não encontrada.",
        duration: 3,
      });
      return;
    }
    setEditingVaga(vaga);
    form.setFieldsValue({
      title: vaga.title,
      company: vaga.company,
      location: vaga.location,
      contract_type: vaga.contract_type,
      description: vaga.description,
      responsibilities: vaga.responsibilities?.split("|").join("\n") || "",
      requirements: vaga.requirements?.split("|").join("\n") || "",
      benefits: vaga.benefits?.split("|").join("\n") || "",
    });
    setIsModalVisible(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();

      const formattedValues = {
        title: values.title,
        company: values.company,
        location: values.location,
        contract_type: values.contract_type,
        description: values.description,
        responsibilities: values.responsibilities
          .split("\n")
          .filter((item) => item.trim())
          .join("|"),
        requirements: values.requirements
          .split("\n")
          .filter((item) => item.trim())
          .join("|"),
        benefits: values.benefits
          .split("\n")
          .filter((item) => item.trim())
          .join("|"),
      };

      setLoading(true);

      if (editingVaga) {
        const response = await api.put(
          `v1/jobs/${editingVaga.uuid}/`,
          formattedValues,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          notification.success({
            message: "Sucesso",
            description: "Vaga atualizada com sucesso!",
            duration: 3,
          });

          setVagas(
            vagas.map((v) => (v.uuid === editingVaga.uuid ? response.data : v))
          );
        }
      } else {
        const response = await api.post("v1/jobs/", formattedValues, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 201) {
          notification.success({
            message: "Sucesso",
            description: "Vaga criada com sucesso!",
            duration: 3,
          });

          setVagas([...vagas, response.data]);
        }
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      setLoading(false);
      if (error.response?.status === 400) {
        const errors = error.response.data;
        notification.error({
          message: "Erro na validação",
          description: Object.entries(errors)
            .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
            .join("\n"),
          duration: 5,
        });
      } else {
        notification.error({
          message: "Erro",
          description: "Algo correu mal. Tente novamente.",
          duration: 3,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await api.delete(`v1/jobs/${id}/`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 204) {
        notification.success({
          message: "Sucesso",
          description: "Vaga apagada com sucesso!",
          duration: 3,
        });

        setVagas(vagas.filter((v) => v.uuid !== id));
      }
    } catch (error) {
      console.error("Erro ao apagar vaga:", error);
      notification.error({
        message: "Erro",
        description: "Não foi possível apagar a vaga.",
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
          <span className="text-gray-400 text-sm">Carregando vagas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-row justify-between items-center">
          <div className="border-l-4 border-emerald-500 pl-4">
           
          </div>
          <button
            onClick={handleAddVaga}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Vaga
          </button>
        </div>
      </div>

      {/* Empty State */}
      {vagas.length === 0 && (
        <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <Briefcase className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <h3 className="text-lg font-light text-gray-700 mb-2">
            Nenhuma vaga publicada
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Comece criando sua primeira oportunidade de emprego
          </p>
          <button
            onClick={handleAddVaga}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Criar Primeira Vaga
          </button>
        </div>
      )}

      {/* Jobs Grid */}
      {vagas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {vagas.map((vaga) => (
            <Oportunidade
              key={vaga.uuid}
              vaga={vaga}
              onEdit={() => handleEdit(vaga.uuid)}
              onDelete={() => handleDelete(vaga.uuid)}
            />
          ))}
        </div>
      )}

      {/* Modal com Blur e Larga */}
      <Modal
        title={null}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={900}
        centered
        className="custom-modal"
        modalRender={(modal) => (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              {modal}
            </div>
          </div>
        )}
      >
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <div className="border-l-4 border-emerald-500 pl-3">
              <h2 className="text-xl font-medium text-gray-800">
                {editingVaga ? "Editar Vaga" : "Nova Vaga"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editingVaga 
                  ? "Actualize as informações da vaga" 
                  : "Preencha os dados para criar uma nova oportunidade"}
              </p>
            </div>
            <button
              onClick={() => setIsModalVisible(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
            
            </button>
          </div>

          {/* Form */}
          <Form form={form} layout="vertical" className="space-y-4">
            <Form.Item
              name="title"
              label={<span className="text-sm font-medium text-gray-700">Título da Vaga</span>}
              rules={[{ required: true, message: "Este campo é obrigatório." }]}
            >
              <Input 
                placeholder="Ex: Desenvolvedor Frontend React"
                className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 h-11"
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="company"
                label={<span className="text-sm font-medium text-gray-700">Empresa</span>}
                rules={[{ required: true, message: "Este campo é obrigatório." }]}
              >
                <Input 
                  placeholder="Ex: Tech Solutions"
                  className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 h-11"
                />
              </Form.Item>

              <Form.Item
                name="location"
                label={<span className="text-sm font-medium text-gray-700">Localização</span>}
                rules={[{ required: true, message: "Este campo é obrigatório." }]}
              >
                <Input 
                  placeholder="Ex: Maputo, Moçambique"
                  className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 h-11"
                />
              </Form.Item>
            </div>

            <Form.Item
              name="contract_type"
              label={<span className="text-sm font-medium text-gray-700">Tipo de Contrato</span>}
              rules={[{ required: true, message: "Este campo é obrigatório." }]}
            >
              <Select 
                placeholder="Selecione o tipo de contrato"
                className="rounded-lg h-11"
                dropdownClassName="rounded-lg"
              >
                <Select.Option value="Tempo integral">Tempo integral</Select.Option>
                <Select.Option value="Meio periodo">Meio período</Select.Option>
                <Select.Option value="Freelancer">Freelancer</Select.Option>
                <Select.Option value="Estagio">Estágio</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label={<span className="text-sm font-medium text-gray-700">Descrição da Vaga</span>}
              rules={[{ required: true, message: "Este campo é obrigatório." }]}
            >
              <TextArea
                rows={4}
                placeholder="Descreva a vaga, empresa e o que procuram..."
                className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
              />
            </Form.Item>

            <Form.Item
              name="responsibilities"
              label={<span className="text-sm font-medium text-gray-700">Responsabilidades (uma por linha)</span>}
              rules={[{ required: true, message: "Este campo é obrigatório." }]}
            >
              <TextArea
                rows={4}
                placeholder="Desenvolver interfaces modernas&#10;Trabalhar em equipe&#10;Garantir boas práticas..."
                className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
              />
            </Form.Item>

            <Form.Item
              name="requirements"
              label={<span className="text-sm font-medium text-gray-700">Requisitos (um por linha)</span>}
              rules={[{ required: true, message: "Este campo é obrigatório." }]}
            >
              <TextArea
                rows={4}
                placeholder="Experiência com React&#10;Conhecimento de Git&#10;Noções de UX/UI..."
                className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
              />
            </Form.Item>

            <Form.Item
              name="benefits"
              label={<span className="text-sm font-medium text-gray-700">Benefícios (um por linha)</span>}
              rules={[{ required: true, message: "Este campo é obrigatório." }]}
            >
              <TextArea
                rows={4}
                placeholder="Ambiente dinâmico&#10;Crescimento profissional&#10;Remuneração compatível..."
                className="rounded-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
              />
            </Form.Item>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
              <button
                onClick={() => setIsModalVisible(false)}
                className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleModalSubmit}
                disabled={loading}
                className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingVaga ? "Salvar" : "Criar"}
              </button>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
}